import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, User } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV_LINKS = [
  { label: "Expeditions", to: "/expeditions" },
  { label: "About", to: "/about" },
  { label: "The Process", to: "/process" },
  { label: "Dispatch Circle", to: "/dispatch-circle" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-carbon/95 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="container-dh flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <div className="h-12 w-12 rounded-full overflow-hidden flex-shrink-0" style={{ filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.5))" }}>
            <img src="/logo.png" alt="Dispatch Hunts" className="h-full w-full object-cover" />
          </div>
          <div className="hidden sm:block">
            <span className="font-display text-lg font-bold text-bone tracking-tight">DISPATCH</span>
            <span className="font-display text-lg font-bold text-brass tracking-tight">HUNTS</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`font-label text-[13px] font-semibold tracking-[0.15em] uppercase transition-colors
                ${location.pathname === link.to ? "text-brass" : "text-bone/70 hover:text-bone"}`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <a href="tel:4065805366" className="text-bone/60 hover:text-brass transition-colors" data-testid="nav-phone">
            <Phone className="w-4 h-4" />
          </a>
          {user && user.id ? (
            <Link to="/circle/dashboard" className="text-bone/60 hover:text-brass transition-colors" data-testid="nav-account">
              <User className="w-4 h-4" />
            </Link>
          ) : (
            <Link to="/login" className="text-bone/60 hover:text-brass transition-colors font-label text-[11px] tracking-wider uppercase" data-testid="nav-login-link">
              Sign In
            </Link>
          )}
          <Link to="/apply" className="btn-brass !py-3 !px-6 !text-xs" data-testid="nav-apply-btn">
            Apply
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-bone p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-carbon/98 backdrop-blur-lg border-t border-bone/10" data-testid="mobile-menu">
          <div className="container-dh py-8 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block font-label text-sm font-semibold tracking-[0.15em] uppercase text-bone/70 hover:text-brass py-3 border-b border-bone/5 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-6">
              <Link to="/apply" className="btn-brass w-full text-center">
                Apply for a Consultation
              </Link>
            </div>
            <a href="tel:4065805366" className="flex items-center gap-2 text-bone/50 font-body text-sm pt-4">
              <Phone className="w-4 h-4" /> 406-580-5366
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
